import { useCallback, useEffect, useState } from 'react';
import {
    Alert, Box, Button, Chip, CircularProgress, Input, Option, Select, Sheet,
    Stack, Table, Typography,
} from '@mui/joy';
import { useNavigate } from 'react-router';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';

import { useAdmin } from '../../../AdminContext';
import { APIAdminAdsApplications, adminAdsError } from '../../../api_admin_ads';
import AdStatusChip from '../../../components/Ads/AdStatusChip';
import { PRICING_LABELS, formatMoney } from '../../../components/Ads/adsConstants';

const STATUS_OPTIONS = [
    { value: '', label: 'Все статусы' },
    { value: 'draft', label: 'Черновики' },
    { value: 'moderation', label: 'На модерации' },
    { value: 'active', label: 'Идут показы' },
    { value: 'rejected', label: 'Отклонены' },
    { value: 'changes_requested', label: 'Требуют правок' },
];

export default function AdminAdsApplicationsPage() {
    const navigate = useNavigate();
    const { adminToken: token, has } = useAdmin();

    const [items, setItems] = useState([]);
    const [counters, setCounters] = useState({});
    const [pages, setPages] = useState(1);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [status, setStatus] = useState('');
    const [onlyManager, setOnlyManager] = useState(true);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const load = useCallback(async () => {
        if (!token) return;
        try {
            setLoading(true);
            setError(null);
            const res = await APIAdminAdsApplications(token, {
                status: status || undefined,
                created_by_manager: onlyManager ? 'true' : undefined,
                page, page_size: 30,
            });
            setItems(res?.items || []);
            setCounters(res?.counters || {});
            setPages(res?.pages || 1);
            setTotal(res?.total || 0);
        } catch (e) {
            setError(adminAdsError(e, 'Не удалось загрузить заявки'));
        } finally {
            setLoading(false);
        }
    }, [token, status, onlyManager, page]);

    useEffect(() => { load(); }, [load]);

    return (
        <Box>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Box>
                    <Typography level="h4" sx={{ mb: 0.5 }}>Заявки на рекламу</Typography>
                    <Typography level="body-sm" color="neutral">Найдено: {total}</Typography>
                </Box>
                {has('ads.manage') && (
                    <Button startDecorator={<AddRoundedIcon />}
                            onClick={() => navigate('/admin/ads/applications/new')}>
                        Новая заявка
                    </Button>
                )}
            </Stack>

            {error && <Alert color="danger" sx={{ mb: 2 }}>{error}</Alert>}

            <Sheet variant="outlined" sx={{ p: 1.5, borderRadius: 'md', mb: 2 }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems="center">
                    <Select size="sm" value={status} sx={{ minWidth: 200 }}
                            onChange={(_, v) => { setStatus(v ?? ''); setPage(1); }}>
                        {STATUS_OPTIONS.map((o) => (
                            <Option key={o.value} value={o.value}>{o.label}</Option>
                        ))}
                    </Select>
                    <Chip variant={onlyManager ? 'solid' : 'outlined'} color="primary"
                          onClick={() => { setOnlyManager((v) => !v); setPage(1); }}
                          sx={{ cursor: 'pointer' }}>
                        Только созданные менеджером
                    </Chip>
                    <Box sx={{ flex: 1 }} />
                    <Stack direction="row" spacing={1}>
                        <Chip size="sm" color="neutral" variant="soft">черновики: {counters.draft ?? 0}</Chip>
                        <Chip size="sm" color="warning" variant="soft">модерация: {counters.moderation ?? 0}</Chip>
                        <Chip size="sm" color="success" variant="soft">активные: {counters.active ?? 0}</Chip>
                        <Chip size="sm" color="danger" variant="soft">отклонены: {counters.rejected ?? 0}</Chip>
                    </Stack>
                </Stack>
            </Sheet>

            {loading ? (
                <Stack alignItems="center" sx={{ py: 6 }}><CircularProgress /></Stack>
            ) : (
                <Sheet variant="outlined" sx={{ borderRadius: 'md', overflow: 'auto' }}>
                    <Table hoverRow stickyHeader sx={{ '--TableCell-paddingY': '10px' }}>
                        <thead>
                            <tr>
                                <th style={{ width: 60 }}>ID</th>
                                <th>Кампания</th>
                                <th style={{ width: 150 }}>Статус</th>
                                <th style={{ width: 130 }}>Охват</th>
                                <th style={{ width: 110 }}>Оплата</th>
                                <th style={{ width: 130 }}>Бюджет/сут</th>
                                <th style={{ width: 120 }}>Создана</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((c) => (
                                <tr key={c.id} style={{ cursor: 'pointer' }}
                                    onClick={() => navigate(`/admin/ads/applications/${c.id}`)}>
                                    <td>{c.id}</td>
                                    <td>
                                        <Typography level="body-sm" sx={{ fontWeight: 600 }}>{c.name}</Typography>
                                    </td>
                                    <td><AdStatusChip status={c.status} /></td>
                                    <td>
                                        <Typography level="body-xs">{c.geo_scope_display || c.geo_scope || '—'}</Typography>
                                    </td>
                                    <td>{PRICING_LABELS[c.pricing_model]}</td>
                                    <td>{formatMoney(c.daily_budget)} ₽</td>
                                    <td>
                                        {c.date_create
                                            ? format(new Date(c.date_create), 'd MMM yy', { locale: ru })
                                            : '—'}
                                    </td>
                                </tr>
                            ))}
                            {items.length === 0 && (
                                <tr>
                                    <td colSpan={7}>
                                        <Typography level="body-sm" color="neutral"
                                                    sx={{ py: 3, textAlign: 'center' }}>
                                            Заявок нет
                                        </Typography>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </Table>
                </Sheet>
            )}

            {pages > 1 && (
                <Stack direction="row" spacing={1} justifyContent="center" sx={{ mt: 2 }}>
                    <Button size="sm" variant="outlined" color="neutral"
                            disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Назад</Button>
                    <Typography level="body-sm" sx={{ alignSelf: 'center' }}>{page} / {pages}</Typography>
                    <Button size="sm" variant="outlined" color="neutral"
                            disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>Вперёд</Button>
                </Stack>
            )}
        </Box>
    );
}